// The scope picker: one control showing the estate as a tree (ZENITH.md §18,
// §6.1). "All sites", then organizations as headings only when more than one
// is visible, customers as headings, sites beneath. Typing filters the tree
// by name and slug, and a site by a name it used to carry too (ADR-0487
// decision 4a), and keeps a match's parents. Names come from the estate,
// refreshed on every open and after every write and never stored, so a
// rename shows at once — on the button as well as in the menu (#192). The
// prototype's decision (one list, customers over sites), with search and the
// organization level added. A site whose customer is not visible is listed at
// the top level rather than dropped (#1393).
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { api } from "../api/client";
import { describe, type Problem } from "../api/problem";
import type { components } from "../api/schema";
import { useEstate, type Lists } from "./estate";
import { ScopeLink, useScope, type Scope } from "./scope";
import { Glyph, OpenIcon } from "../icons";
import { wordFor } from "../api/openEnum";

type Customer = components["schemas"]["Customer"];
type Site = components["schemas"]["Site"];
type SearchResults = components["schemas"]["SearchResults"];

export type { Lists };

/** One pickable row of the flattened tree, in display order. */
export interface Row {
  key: string;
  kind: "all" | "org" | "customer" | "site";
  name: string;
  scope: Scope;
  depth: number;
  /** For "site" rows, the customer's name, for the tooltip path. */
  path: string;
  /** For a "site" row found by a name it used to carry and not by its own
   * (ADR-0487 decision 4a): that name, written "…, formerly …". */
  formerly?: string;
}

const norm = (s: string) => s.toLowerCase();
const matches = (q: string, name: string, slug?: string) => q === "" || norm(name).includes(q) || (slug !== undefined && norm(slug).includes(q));

/** The name a site used to carry that the query found it by, where its own
 * name and slug did not: an exact one first, as the server's search orders
 * them (ADR-0487 decision 4a). Undefined where the site matched by its own
 * name, or not at all. */
export function formerMatch(q: string, s: Site): string | undefined {
  if (matches(q, s.name, s.slug)) return undefined;
  return (s.former_names.find((f) => norm(f.name) === q) ?? s.former_names.find((f) => norm(f.name).includes(q)))?.name;
}
const siteMatches = (q: string, s: Site) => matches(q, s.name, s.slug) || formerMatch(q, s) !== undefined;

/** Flattens the tree into rows, applying the filter: a row stays when it
 * matches, when an ancestor matches, or when a descendant does.
 *
 * `allOrgs` keeps the organization level even when there is only one. The rail
 * hides it there because it is a heading with nothing to distinguish — but an
 * organization is a scope a role can be granted at, and one that will still be
 * the right answer when a second customer appears, so a form that offers scopes
 * must offer it (§6.2). Navigation and authorization want different trees. */
export function buildRows(l: Lists, query: string, opts?: { allOrgs?: boolean }): Row[] {
  const q = norm(query.trim());
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  const showOrgs = opts?.allOrgs === true || l.orgs.length > 1;
  const rows: Row[] = [];
  if (q === "") rows.push({ key: "all", kind: "all", name: "All sites", scope: {}, depth: 0, path: "" });
  const sitesOf = (c: Customer) => l.sites.filter((s) => s.customer_id === c.id).sort(byName);
  const customerRows = (customers: Customer[], depth: number, orgMatched: boolean) => {
    for (const c of customers.sort(byName)) {
      const cMatched = orgMatched || matches(q, c.name, c.slug);
      const sites = sitesOf(c).filter((s) => cMatched || siteMatches(q, s));
      if (!cMatched && sites.length === 0) continue;
      rows.push({ key: "c:" + c.id, kind: "customer", name: c.name, scope: { orgId: c.org_id, customerId: c.id }, depth, path: "" });
      for (const s of sites) rows.push({ key: "s:" + s.id, kind: "site", name: s.name, scope: { orgId: s.org_id, customerId: s.customer_id, siteId: s.id }, depth: depth + 1, path: c.name,
        formerly: cMatched ? undefined : formerMatch(q, s) });
    }
  };
  if (showOrgs) {
    for (const o of [...l.orgs].sort(byName)) {
      const oMatched = matches(q, o.name, o.slug);
      const before = rows.length;
      rows.push({ key: "o:" + o.id, kind: "org", name: o.name, scope: { orgId: o.id }, depth: 0, path: "" });
      customerRows(l.customers.filter((c) => c.org_id === o.id), 1, oMatched);
      if (!oMatched && rows.length === before + 1) rows.pop();
    }
    // Customers whose organization is not in the caller's list still show.
    const seen = new Set(l.orgs.map((o) => o.id));
    customerRows(l.customers.filter((c) => !seen.has(c.org_id)), 0, false);
  } else {
    customerRows([...l.customers], 0, false);
  }
  // Sites whose customer is not in the caller's list still show, as rows of
  // their own. A grant at a site makes the site visible and its customer not
  // (§6.2, §6.3), so a tree that hung every site off a visible customer offered
  // a site-scoped person nothing to pick, and every per-site page was out of
  // reach (#1393). No heading and no path: a site row carries no customer
  // name, and showing one would disclose what the 404 rule withholds.
  const listed = new Set(l.customers.map((c) => c.id));
  for (const s of l.sites.filter((x) => !listed.has(x.customer_id) && siteMatches(q, x)).sort(byName)) {
    rows.push({ key: "s:" + s.id, kind: "site", name: s.name, scope: { orgId: s.org_id, customerId: s.customer_id, siteId: s.id }, depth: 0, path: "", formerly: formerMatch(q, s) });
  }
  return rows;
}

const KIND: Record<Row["kind"], string> = { all: "", org: "Organization", customer: "Customer", site: "Site" };

/** The searchable tree, without an opinion about who opened it. The rail shows
 * every scope and picks one to look at; a form shows the scopes a capability
 * reaches and picks one to act at (`ScopeChooser`). Filtering, the arrow keys
 * and the row rendering are the same question either way, and were the rail's
 * private business until there was a second caller. */
export function ScopeMenu({ lists, allOrgs, keep, isCurrent, startAt, onPick, onClose }: {
  lists?: Lists;
  allOrgs?: boolean;
  /** Which rows this caller may pick. Absent means all of them. */
  keep?: (r: Row) => boolean;
  isCurrent?: (r: Row) => boolean;
  /** The row the menu opens on: highlighted, named as the active descendant
   * and scrolled into view, until the person moves the highlight or types.
   * Absent, or matching no row, the menu opens on its first row (#1722). */
  startAt?: (r: Row) => boolean;
  onPick(r: Row): void;
  onClose(): void;
}) {
  const [query, setQuery] = useState("");
  // Undefined until the person moves the highlight or types. Until then the
  // highlight is derived from `startAt`, so it lands on that row whenever the
  // row comes to exist: the estate's lists can arrive after the menu opens.
  const [rawCursor, setCursor] = useState<number | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const { adopt } = useEstate(false);

  useEffect(() => { setTimeout(() => input.current?.focus(), 0); }, []);
  const rows = useMemo(() => {
    const all = lists ? buildRows(lists, query, { allOrgs }) : [];
    return keep ? all.filter(keep) : all;
  }, [lists, query, allOrgs, keep]);

  // Past the drain's bound the lists do not hold every scope (#188), so what
  // was typed can be asked of the server: `GET /v1/search`, which looks among
  // everything the caller can read (#728, ADR-0109). It is asked when the
  // person says so, by the row below the list, and never as they type: when a
  // box may ask on its own, and after how long, is not ruled. Only where the
  // lists were cut: a list that was read whole is searched whole right here.
  const term = query.trim();
  const [found, setFound] = useState<Found>();
  const asked = found?.term === term ? found : undefined;
  const extra = useMemo(() => {
    if (asked?.state !== "answered") return [];
    const listed = new Set(rows.map((r) => r.key));
    return asked.rows.filter((r) => !listed.has(r.key) && (!keep || keep(r)));
  }, [asked, rows, keep]);
  const all = useMemo(() => [...rows, ...extra], [rows, extra]);
  const askAt = lists?.more && term !== "" && !asked ? all.length : -1;
  const ask = () => {
    setFound({ term, state: "asking", rows: [], more: false, notSearched: [] });
    const answer = (next: Found) => setFound((f) => (f?.term === term ? next : f));
    api.GET("/search", { params: { query: { q: term } } }).then(({ data, error, response }) => {
      if (!data || error) { answer({ term, state: "failed", rows: [], more: false, notSearched: [], problem: describe(error, response.status) }); return; }
      answer({
        term, state: "answered", rows: data.items.flatMap(hitRow), notSearched: data.not_searched,
        more: data.more.includes("customer") || data.more.includes("site"),
      });
    }, () => answer({ term, state: "failed", rows: [], more: false, notSearched: [], problem: describe(undefined) }));
  };
  const pick = (r: Row) => {
    // A scope the lists never held is read by its own id so that the rail and
    // every form can name it (`adopt`); the pick does not wait for that.
    if (extra.includes(r)) adopt({ customerId: r.scope.customerId, siteId: r.scope.siteId });
    onPick(r);
  };

  // The highlighted row, clamped to the rows the filter left: derived where it
  // is read rather than corrected after the fact.
  const start = useMemo(() => (startAt ? rows.findIndex(startAt) : -1), [rows, startAt]);
  const last = askAt >= 0 ? askAt : all.length - 1;
  const cursor = Math.min(rawCursor ?? Math.max(start, 0), Math.max(last, 0));
  // Bring the opening row to the top of the list, so what hangs under it is
  // what shows beneath it. Only while the highlight is still the derived one:
  // a person who has moved it or typed is not scrolled back. The list scrolls
  // and the search field does not (`theme.css`), so the field stays in view.
  const startKey = rawCursor === undefined && start >= 0 ? rows[start]?.key : undefined;
  useEffect(() => {
    if (startKey !== undefined) document.getElementById(listId + "-" + startKey)?.scrollIntoView?.({ block: "start" });
  }, [startKey, listId]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(Math.min(cursor + 1, last)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setCursor(Math.max(cursor - 1, 0)); }
    if (e.key === "Enter") {
      if (cursor === askAt) { e.preventDefault(); ask(); }
      else if (all[cursor]) { e.preventDefault(); pick(all[cursor]); }
    }
  };
  const option = (r: Row, i: number) => (
    <button type="button" key={r.key} id={listId + "-" + r.key} role="option" tabIndex={-1} aria-selected={isCurrent?.(r) ?? false}
      className={"picker-" + r.kind + (isCurrent?.(r) ? " on" : "") + (i === cursor ? " hl" : "")}
      style={{ paddingLeft: 8 + r.depth * 18 }} title={r.path ? `${r.path} / ${r.name}` : undefined}
      onMouseEnter={() => setCursor(i)} onClick={() => pick(r)}>{r.name}{r.formerly && <span className="muted">, formerly {r.formerly}</span>}</button>
  );
  const active = cursor === askAt ? listId + "-ask" : all[cursor] ? listId + "-" + all[cursor].key : undefined;
  return (
    <>
      {/* A click-away layer, not a control: Escape closes the menu for the keyboard. */}
      <div className="backdrop" role="presentation" onClick={onClose} />
      {/* The search field is a combobox and keeps focus: the arrows move the
          highlight, and the field's aria-activedescendant names the highlighted
          row, so a screen reader announces it as the cursor moves (R22, #204).
          On the listbox, which never has focus, the attribute announced nothing. */}
      <div className="rail-menu picker" role="presentation" onKeyDown={onKey}>
        <input ref={input} className="picker-search" type="search" placeholder="Filter by name or slug" aria-label="Filter scopes" value={query}
          role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
          aria-activedescendant={active}
          onChange={(e) => { setQuery(e.target.value); setCursor(0); }} />
        <div role="listbox" id={listId} aria-label="Scope">
          {!lists && <span className="muted">Loading…</span>}
          {lists && all.length === 0 && askAt < 0 && !asked && <span className="muted">Nothing matches.</span>}
          {rows.map(option)}
          {extra.length > 0 && <span className="muted" role="presentation">Found by search</span>}
          {extra.map((r, i) => option(r, rows.length + i))}
          {askAt >= 0 && (
            <button type="button" id={listId + "-ask"} role="option" tabIndex={-1} aria-selected={false}
              className={"picker-ask" + (cursor === askAt ? " hl" : "")}
              onMouseEnter={() => setCursor(askAt)} onClick={ask}>Search all customers and sites for “{term}”</button>
          )}
          {asked && <SearchNote found={asked} extra={extra.length} />}
          {/* Typing filters what was loaded; it cannot find what was not (#188).
              The search row above can (#728). */}
          {lists?.more && <span className="muted">Not every scope is listed here. Type a name, then search, to find one that is not. The <ScopeLink to="/customers" onClick={onClose}>Customers</ScopeLink> and <ScopeLink to="/sites" onClick={onClose}>Sites</ScopeLink> pages list everything.</span>}
        </div>
      </div>
    </>
  );
}

/** What the server answered for one term, for the rows the menu did not load. */
interface Found {
  term: string;
  state: "asking" | "answered" | "failed";
  rows: Row[];
  /** A class the menu shows matched more than the answer carried. */
  more: boolean;
  notSearched: SearchResults["not_searched"];
  problem?: Problem;
}

/** A search hit as a row of the menu. Only a customer or a site is a scope a
 * person can pick; a device or a network is found by the same search and is
 * not one. The path is the parent's name where the server served it: it names
 * a hit's customer or organization only to a caller whose role reaches it
 * (§6.3), so a site found by a person granted at that site alone has none. */
function hitRow(h: SearchResults["items"][number]): Row[] {
  if (h.kind === "customer" && h.id) return [{ key: "c:" + h.id, kind: "customer", name: h.name ?? "", scope: { orgId: h.org_id, customerId: h.id }, depth: 0, path: h.org_name ?? "" }];
  if (h.kind === "site" && h.id) return [{ key: "s:" + h.id, kind: "site", name: h.name ?? "", scope: { orgId: h.org_id, customerId: h.customer_id, siteId: h.id }, depth: 0, path: h.customer_name ?? "", formerly: h.matched_former_name }];
  return [];
}

// Restates ADR-0109 decisions 1 and 4, as ADR-0487 decision 4a and ADR-0551 amend 1 — the search matches what
// this service holds, a site's former names and a whole ID among it, so an access point's name and a connected
// device's model are not searched, and the answer says so instead of
// showing an empty list that reads as "absent". Nothing found is one sentence whether the thing
// does not exist or is outside the caller's scope, and no number of matches is ever shown. The
// list of what is not searched is the server's (`not_searched`), not this file's.
const NOT_SEARCHED: Record<components["schemas"]["SearchResultsNotSearched"], string> = {
  access_point_name: "an access point's name",
  connected_device_model: "a connected device's model",
  // Restates ADR-0324 decision 2 — a client's address is looked for at one site, not by this search.
  client_address: "a client's address",
  // Restates ADR-0551 decision 3a — no device is found by an address, its own or the one it is seen from.
  device_address: "a device's address",
};

function SearchNote({ found, extra }: { found: Found; extra: number }) {
  if (found.state === "asking") return <span className="muted" role="status">Searching…</span>;
  if (found.state === "failed") return <span className="muted" role="status">The search did not answer. {found.problem?.message}</span>;
  const skipped = found.notSearched.map((n) => wordFor(NOT_SEARCHED, n));
  return (
    <span className="muted" role="status">
      {extra === 0 && <>No other customer or site you can see matches “{found.term}”. </>}
      {found.more && <>More match than are shown. Type more of the name to narrow them. </>}
      {skipped.length > 0 && <>A customer is found by its name, its slug or its whole ID, and a site by its name, its slug, its whole ID or a name it used to carry. Not searched: {listOf(skipped)}.</>}
    </span>
  );
}

/** "a, b and c", for a sentence. */
function listOf(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
}

export default function ScopePicker() {
  const { scope, setScope } = useScope();
  const [open, setOpen] = useState(false);
  // The shared copy names the button on first paint; opening reads it again.
  const { lists, refresh } = useEstate();
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // The current pick, named live.
  const current = useMemo(() => {
    if (!lists) return undefined;
    if (scope.siteId) { const s = lists.sites.find((x) => x.id === scope.siteId); return s && { kind: "site" as const, name: s.name, path: lists.customers.find((c) => c.id === s.customer_id)?.name }; }
    if (scope.customerId) { const c = lists.customers.find((x) => x.id === scope.customerId); return c && { kind: "customer" as const, name: c.name, path: lists.orgs.find((o) => o.id === c.org_id)?.name }; }
    if (scope.orgId) { const o = lists.orgs.find((x) => x.id === scope.orgId); return o && { kind: "org" as const, name: o.name, path: undefined }; }
    return { kind: "all" as const, name: "All sites", path: undefined };
  }, [lists, scope]);
  const isCurrent = (r: Row) => (r.scope.siteId ?? r.scope.customerId ?? r.scope.orgId ?? "") === (scope.siteId ?? scope.customerId ?? scope.orgId ?? "");
  // Scoped to an organization, the menu opens on that organization's row
  // (#1722). At a customer, a site or All sites it opens at the top, as it
  // did: only the organization case was asked for. The row is the scope the
  // page is already on, so starting there discloses nothing (§6.3); where the
  // estate lists no such row, as with one visible organization, there is
  // nothing to start at and the menu opens at the top.
  const orgOnly = scope.orgId !== undefined && scope.customerId === undefined && scope.siteId === undefined ? scope.orgId : undefined;
  const startAt = useMemo(() => (orgOnly === undefined ? undefined : (r: Row) => r.kind === "org" && r.scope.orgId === orgOnly), [orgOnly]);
  // A scope the estate does not name — remembered from another sign-in, or
  // pasted from someone who can see it — is dropped rather than shown as "…"
  // (#184). Only when the whole estate was drained: past the drain's bound the
  // scope may be real and unlisted, and nothing here can tell. The server
  // still answers the page 404-shaped either way (§6.3); this is a
  // convenience, not a second authorization.
  useEffect(() => { if (lists && !lists.more && current === undefined) setScope({}); }, [lists, current, setScope]);

  const pick = (r: Row) => { setScope(r.scope); setOpen(false); };

  const label = current ? current.name : scope.siteId || scope.customerId || scope.orgId ? "…" : "All sites";
  const title = current?.path ? `${current.path} / ${current.name}` : current?.name;
  return (
    <>
      <span className="rail-label">Scope</span>
      <span className="seg rail-scope">
        <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={"Scope: " + (current && current.kind !== "all" ? KIND[current.kind] + " " : "") + label} title={title} onClick={() => setOpen(!open)}>
          {current && current.kind !== "all" && <span className="seg-kind">{KIND[current.kind]}</span>}{label}<Glyph icon={OpenIcon} size={12} className="seg-caret" />
        </button>
        {open && <ScopeMenu lists={lists} isCurrent={isCurrent} startAt={startAt} onPick={pick} onClose={() => setOpen(false)} />}
      </span>
    </>
  );
}
